import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs'
import AddModel from '../../../components/Add/AddModel'
import AddBottomButton from '../../../components/Add/AddBottomButton'
import EmptyListingScreen from '../../../components/emptyScreens/EmptyListingScreen'
import useApi from '../../../services/useApi'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../components/toastMessage/toastSlice'
import BundlePlanCard, { BundlePlan } from './BundlePlanCard'
import BundlePlanCardShimmer from './BundlePlanCardShimmer'
import DynamicFilterPanel, { DynamicFilterConfig } from '../../../components/DynamicComponent/DynamicFilterPanel'

interface BundleActivationPlansState {
  mno_id?: string
  telecom_name?: string
  wallet_type?: string
  phone_number?: string
  country_code?: string
}

interface BundlePlanFilters {
  price_min?: number | null
  price_max?: number | null
  plan_type?: string[] | null
}

const BUNDLE_PLAN_FILTER_CONFIG: DynamicFilterConfig = {
  price: {
    label: 'Price Range',
    type: 'range',
    searchKey: { min: 'price_min', max: 'price_max' },
    subFilters: {
      min: { label: 'Price Min', placeholder: '00.00 AFA', searchKey: 'price_min' },
      max: { label: 'Price Max', placeholder: '1000.00 AFA', searchKey: 'price_max' },
    },
  },
  planType: {
    label: 'Plan Type',
    type: 'checkbox',
    options: ['Voice', 'Data', 'SMS'],
    searchKey: 'plan_type',
  },
}

function BundleActivationPlans() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getAPI } = useApi()
  const state = (location.state || {}) as BundleActivationPlansState

  const [plans, setPlans] = useState<BundlePlan[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [filters, setFilters] = useState<BundlePlanFilters>({})

  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const filtersActive =
    (filters.price_min !== undefined && filters.price_min !== null) ||
    (filters.price_max !== undefined && filters.price_max !== null) ||
    (Array.isArray(filters.plan_type) && filters.plan_type.length > 0)
  // Hide the Filter trigger only when there's genuinely nothing to filter (no plans and no
  // active filters) - keep it available when filtering produced zero results so users can
  // still adjust/clear their filters.
  const hasUnfilteredResults = plans.length > 0 || filtersActive

  // DynamicFilterPanel only restores its displayed values from a `searchParams`
  // prop (it isn't URL-driven here) - mirror the applied filters into one so
  // reopening the panel shows what's currently applied instead of resetting.
  const filterSearchParams = useMemo(() => {
    const params = new URLSearchParams()
    if (filters.price_min !== undefined && filters.price_min !== null) params.set('price_min', String(filters.price_min))
    if (filters.price_max !== undefined && filters.price_max !== null) params.set('price_max', String(filters.price_max))
    if (Array.isArray(filters.plan_type) && filters.plan_type.length > 0) params.set('plan_type', filters.plan_type.join(','))
    return params
  }, [filters])

  // Guard against landing here directly without having detected an MNO first.
  useEffect(() => {
    if (!state.mno_id) {
      navigate('/services/bundle-activation', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchPlans = async (pageNumber: number) => {
    if (!state.mno_id) return
    if (pageNumber === 1) setLoading(true)
    else setLoadingMore(true)

    const params = new URLSearchParams({ mno_id: state.mno_id, page_number: String(pageNumber) })
    if (filters.price_min !== undefined && filters.price_min !== null) params.set('price_min', String(filters.price_min))
    if (filters.price_max !== undefined && filters.price_max !== null) params.set('price_max', String(filters.price_max))
    if (Array.isArray(filters.plan_type) && filters.plan_type.length > 0) params.set('plan_type', filters.plan_type.join(','))

    const res = await getAPI(`${endpoints.bundlePlansList}?${params.toString()}`)
    if (res?.error) {
      if (pageNumber === 1) {
        setPlans([])
        setTotalCount(0)
      }
      setHasMore(false)
      dispatch(showToast({ type: 'error', message: res.data?.data?.message || 'Failed to load bundle plans' }))
    } else {
      const fetchedPlans: BundlePlan[] = res?.data?.data || []
      const total = res?.data?.total_count || 0
      setTotalCount(total)
      setPlans((prev) => {
        const combined = pageNumber === 1 ? fetchedPlans : [...prev, ...fetchedPlans]
        setHasMore(combined.length < total)
        return combined
      })
    }

    if (pageNumber === 1) {
      setSelectedPlanId('')
      setLoading(false)
    } else {
      setLoadingMore(false)
    }
  }

  // Re-fetch from the first page whenever the MNO or filters change.
  useEffect(() => {
    setPage(1)
    setHasMore(true)
    fetchPlans(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.mno_id, filters])

  // Fetch subsequent pages as the user scrolls near the bottom of the list.
  useEffect(() => {
    if (page > 1) fetchPlans(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  // Infinite scroll: observe a sentinel at the end of the list against the
  // AddModel scroll container (the actual scrollable ancestor, not the window).
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore) return

    const root = containerRef.current?.closest('.add-model-scroll-container') as HTMLElement | null

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setPage((prev) => prev + 1)
        }
      },
      { root, rootMargin: '150px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loading, loadingMore])

  const handleConfirmPlan = () => {
    // Actual bundle activation submission is handled by MM-3074 (Proceed with Recharge).
    dispatch(
      showToast({
        type: 'info',
        message: 'Bundle activation confirmation is coming soon.',
      })
    )
  }

  return (
    <div>
      <Breadcrumbs
        navPath={['Services', 'Bundle Activation', 'View Plans']}
        heading="View Plans"
        links={['/services/bundle-activation', '/services/bundle-activation', '/services/bundle-activation/plans']}
        linkStates={[
          undefined,
          {
            phone_number: state.phone_number,
            country_code: state.country_code,
            mno_id: state.mno_id,
            telecom_name: state.telecom_name,
            wallet_type: state.wallet_type,
          },
          undefined,
        ]}
        hasAddPrivilege={true}
        fullNavPath
      />
      <AddModel>
        <div className="flex flex-col min-h-full" ref={containerRef}>
          <div className="flex items-center justify-between mb-4 shrink-0 sticky top-0 z-10 bg-white py-2">
            <p className="text-medium text-secondary_text">
              Telecom: <span className="text-body1 font-semibold text-primary_text">{state.telecom_name}</span>
            </p>
            <div className="flex items-center gap-4">
              {hasUnfilteredResults && (
                <DynamicFilterPanel
                  config={BUNDLE_PLAN_FILTER_CONFIG}
                  onApply={(values) => setFilters(values as BundlePlanFilters)}
                  searchParams={filterSearchParams}
                  disabled={loading}
                />
              )}
              <p className="text-medium text-secondary_text whitespace-nowrap">Total Plans: {totalCount}</p>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <BundlePlanCardShimmer key={index} />
              ))}
            </div>
          ) : plans.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyListingScreen
                emptyScreenText={{ heading1: 'No results found', img: 'search_empty_screen_img' }}
                imageHight="h-[269px]"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {plans.map((plan) => (
                <BundlePlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedPlanId === plan.id}
                  onSelect={setSelectedPlanId}
                />
              ))}
              {loadingMore && (
                <>
                  <BundlePlanCardShimmer />
                  <BundlePlanCardShimmer />
                </>
              )}
              {hasMore && <div ref={sentinelRef} />}
            </div>
          )}
        </div>
      </AddModel>
      <AddBottomButton
        buttonName="Confirm Plan"
        buttonWidth="w-[160px]"
        onClick={handleConfirmPlan}
        loading={false}
        disabled={!selectedPlanId}
        testID="confirm-plan"
        align="end"
      />
    </div>
  )
}

export default BundleActivationPlans
